import { createRouter, createWebHistory } from 'vue-router'
import { authUtils } from '@/api/auth'
import { authStateManager } from '@/api/auth'
import { tokenRefreshManager } from '@/main'
import { clearAllAuthState, isTokenExpired as utilsIsTokenExpired } from '@/utils/authUtils'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/Home.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/Login.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/brand-role-management',
      name: 'brandRoleManagement',
      component: () => import('@/views/BrandRoleManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/brand-user-management',
      name: 'brandUserManagement',
      component: () => import('@/views/BrandUserManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/brand-prize-management',
      name: 'brandPrizeManagement',
      component: () => import('@/views/BrandPrizeManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/brand-product-management',
      name: 'brandProductManagement',
      component: () => import('@/views/BrandProductManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/brand-lottery-management',
      name: 'brandLotteryManagement',
      component: () => import('@/views/BrandLotteryManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/brand-lottery-activity/:id',
      name: 'brandLotteryActivityDetail',
      component: () => import('@/views/BrandLotteryActivityDetail.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/brand-member-level-management',
      name: 'brandMemberLevelManagement',
      component: () => import('@/views/BrandMemberLevelManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/brand-member-tag-management',
      name: 'brandMemberTagManagement',
      component: () => import('@/views/BrandMemberTagManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/brand-coupon-management',
      name: 'brandCouponManagement',
      component: () => import('@/views/BrandCouponManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/goods-management',
      name: 'goodsManagement',
      component: () => import('@/views/GoodsManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/brand-settings-management',
      name: 'brandSettingsManagement',
      component: () => import('@/views/BrandSettingsManagement.vue'),
      meta: { requiresAuth: true }
    }
  ]
})

// 路由守卫
router.beforeEach(async (to, from, next) => {
  // 检查是否需要认证
  const requiresAuth = to.meta.requiresAuth !== false // 默认为需要认证
  
  // 获取认证状态 - 直接检查localStorage
  let token = localStorage.getItem('apiToken')
  const currentUser = localStorage.getItem('currentUser')
  const refreshToken = localStorage.getItem('apiToken_refresh')
  
  // 优化：只在需要认证的页面且token确实过期时才刷新
  // 避免在每次路由切换时都检查刷新（包括访问登录页等不需要认证的页面）
  if (requiresAuth && refreshToken) {
    // 如果token不存在或已过期，尝试刷新
    if (!token || utilsIsTokenExpired(token)) {
      try {
        // 如果正在刷新，等待现有刷新完成
        if (tokenRefreshManager.isRefreshing() && tokenRefreshManager.getRefreshPromise()) {
          const newToken = await tokenRefreshManager.getRefreshPromise()
          if (newToken) {
            token = newToken
          } else {
            // 刷新失败，清除所有认证状态
            clearAllTokens()
            token = null // 确保token为null，避免后续判断错误
          }
        } else {
          // 执行新的刷新，使用统一的刷新方法
          const newToken = await refreshTokenInRouter(refreshToken)
          if (newToken) {
            token = newToken
          } else {
            // 刷新失败，清除所有认证状态
            clearAllTokens()
            token = null // 确保token为null，避免后续判断错误
          }
        }
      } catch (error) {
        // 刷新过程中出错，清除所有认证状态
        clearAllTokens()
        token = null // 确保token为null，避免后续判断错误
      }
    }
  }
  
  // 重新获取认证状态（因为可能已经被清除）
  if (!token) {
    token = localStorage.getItem('apiToken')
  }
  const currentUserAfterRefresh = localStorage.getItem('currentUser')
  const isLoggedIn = !!(token && currentUserAfterRefresh)

  // 如果访问登录页
  if (to.path === '/login') {
    if (isLoggedIn) {
      next('/')
    } else {
      next()
    }
    return
  }

  // 如果需要认证但未登录
  if (requiresAuth && !isLoggedIn) {
    // 保存原始目标路径，登录后可以跳转回去
    if (to.path !== '/login') {
      sessionStorage.setItem('redirectAfterLogin', to.fullPath)
    }
    next('/login')
    return
  }

  // 只有在首次访问根路径且已登录时，才尝试刷新用户信息
  if (to.path === '/' && isLoggedIn && from.path === '/login') {
    try {
      await authUtils.refreshUserInfo()
    } catch (error) {
      console.error('❌ 刷新用户信息失败:', error)
      // 只有在用户信息刷新失败且没有有效token时才清除认证状态
              if (!token || utilsIsTokenExpired(token)) {
        authStateManager.clearAuthState()
        sessionStorage.setItem('redirectAfterLogin', to.fullPath)
        next('/login')
        return
      }
    }
  }
  next()
})



// 在路由守卫中刷新token（使用统一的刷新逻辑，避免并发刷新）
async function refreshTokenInRouter(refreshToken: string): Promise<string | null> {
  // 如果正在刷新，直接返回现有的promise
  if (tokenRefreshManager.isRefreshing() && tokenRefreshManager.getRefreshPromise()) {
    return tokenRefreshManager.getRefreshPromise()
  }
  
  // 设置刷新状态
  tokenRefreshManager.setRefreshing(true)
  
  try {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const apiUrl = apiBaseUrl.endsWith('/') 
      ? `${apiBaseUrl}api/auth/refresh`
      : `${apiBaseUrl}/api/auth/refresh`
    
    // 获取当前品牌ID
    let currentBrandId: number | null = null
    try {
      const currentBrand = localStorage.getItem('currentBrand')
      if (currentBrand) {
        const brandData = JSON.parse(currentBrand)
        currentBrandId = brandData.id || null
      }
    } catch (error) {
      console.warn('获取当前品牌ID失败:', error)
    }
    
    const refreshPromise = fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        refreshToken,
        brandId: currentBrandId 
      })
    }).then(async (response) => {
      const data = await response.json()
      
      if (data.success && data.data) {
        const newAccessToken = data.data
        localStorage.setItem('apiToken', newAccessToken)
        return newAccessToken
      } else {
        // 刷新失败时清除所有认证状态
        clearAllTokens()
        return null
      }
    }).catch((error) => {
      console.error('❌ 路由守卫：Token刷新过程中出错:', error)
      // 刷新过程中出错时清除所有认证状态
      clearAllTokens()
      return null
    })
    
    // 保存promise供其他请求复用
    tokenRefreshManager.setRefreshPromise(refreshPromise)
    
    const result = await refreshPromise
    return result
  } finally {
    // 刷新完成，重置状态
    tokenRefreshManager.setRefreshing(false)
    tokenRefreshManager.setRefreshPromise(null)
  }
}

// 清除所有token和认证状态
function clearAllTokens(): void {
  // 强制清除所有认证状态，即使在开发环境也要清除（因为刷新失败意味着token无效）
  localStorage.removeItem('apiToken')
  localStorage.removeItem('apiToken_refresh')
  localStorage.removeItem('currentUser')
  localStorage.removeItem('currentBrand')
  console.log('❌ 路由守卫：已清除所有认证状态')
}

export default router
